import {AfterContentInit, Component, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {FontAwesomeService} from '@services/font-awesome.service';
import {Option} from '@models/select-dropdown-option.interface';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Observable} from 'rxjs/Observable';
import {PackageTypeEnum} from '@models/package-type.enum';
import {Package} from '@models/package.interface';
import 'rxjs/add/operator/map';
import {PackageTemplatesService} from '@services/package-templates.service';
import {InitInformation} from '@models/init-information.interface';
import {Subject} from 'rxjs/Subject';

@Component({
  selector: 'package-details-form',
  templateUrl: './package-details-form.component.html',
  styleUrls: ['./package-details-form.component.scss']
})

export class PackageDetailsFormComponent implements OnInit, AfterContentInit {
  @Input() parent: FormGroup;
  @Input() submitted: boolean;
  @Input() packages: Package[];

  parcelTypes: Observable<Option[]>;

  private unsubscribe: Subject<void> = new Subject();
  public packageTemplates = [];
  public mappedPackageTemplatesArray = [];
  public packageTemplateForm;
  public showPackageTemplateForm = false;
  public showDeletePackageTemplateButton = false;

  constructor(
    public faService: FontAwesomeService,
    private translate: TranslateService,
    private packageTemplatesService: PackageTemplatesService,
  ) {}

  ngOnInit() {
    this.parcelTypes = this.generatePackageTypes();

    if (this.packages.length) {
      this.parent.addControl('packages', PackageDetailsFormComponent.generateForm(this.packages.length));
      this.parent.get('packages').setValue(this.packages);
    } else {
      this.parent.addControl('packages', PackageDetailsFormComponent.generateForm());
    }

    /**
     * Package Templates
     */
    this.makePackageTemplateForm();
    this.getAllPackageTemplates();
  }

  ngAfterContentInit() {
    this.packageDetails.forEach((item, index) => {
      if (item.get('type').value) {
        this.updatePackageDependsOnPackageType(+item.get('type').value, item as FormGroup, index === 0, true);
      }
    });
  }

  generatePackageTypes(): Observable<Option[]> {
    return forkJoin([1, 2, 3, 4].map(data => this.translate.get(`common.packageType.${data}`)))
      .map(translations => translations.map((label, i): Option => ({label, value: (i + 1).toString()})));
  }

  get packageDetails () {
    return (this.parent.get('packages') as FormArray).controls;
  }

  pushNewPackage() {
    (this.parent.get('packages') as FormArray).push(PackageDetailsFormComponent.generateFormGroup());
    const length = (this.parent.get('packages') as FormArray).length;
    const packageType: PackageTypeEnum = +this.getParent(0).get('type').value;
    if (packageType) {
      this.updatePackageDependsOnPackageType(packageType, this.getParent(length - 1) as FormGroup, false);
    }
  }

  removePackage(index) {
    (this.parent.get('packages') as FormArray).removeAt(index);
  }

  static generateForm(packageAmount = 1) {
    const packagesFormArray = new FormArray([PackageDetailsFormComponent.generateFormGroup()]);
    for (let i = 1; i < packageAmount; i++) {
      packagesFormArray.push(PackageDetailsFormComponent.generateFormGroup());
    }
    return packagesFormArray;
  }

  static generateFormGroup() {
    const generateFormControl =
    (initialValue, pattern) => PackageDetailsFormComponent.generateFormControl(initialValue, pattern);

    return new FormGroup({
      height: generateFormControl('', '^[1-9][0-9]*$'),
      length: generateFormControl('', '^[1-9][0-9]*$'),
      quantity: generateFormControl('', '^[1-9]{1,}$|^[1-9][0-9]{1,}$'),
      stackable: new FormControl(false),
      type: new FormControl('', [Validators.required]),
      weight: generateFormControl('', '^[1-9][0-9]*$|^[1-9]*\.[0-9][1-9]{1,2}$|^[1-9]*\.[1-9]{1,2}$|^[0-9]*\.[0-9][1-9]{1,2}$|^[0-9]*\.[1-9]{1,2}$'),
      width: generateFormControl('', '^[1-9][0-9]*$')
    });
  }

  static generateFormControl(initialValue: string, pattern: string) {
    return new FormControl(initialValue, [Validators.required, Validators.pattern(pattern)]);
  }

  getParent(index) {
    return (this.parent.get('packages') as FormArray).at(index);
  }

  resetPackageDetailsForm() {
    this.parent.get('packages').reset();
  }

  handlePackageTypeChange({value}) {
    this.packageDetails.map((element, index) => {
      this.updatePackageDependsOnPackageType(+value, element as FormGroup, index === 0);
    });
  }

  updatePackageDependsOnPackageType(packageType: PackageTypeEnum, element: FormGroup, isFirst: boolean, isAfterInit = false) {
    element.get('type').setValue(`${packageType}`);

    if (!isFirst) {
      element.get('type').disable();
    }

    if (!isAfterInit) {
      element.get('stackable').setValue(packageType === PackageTypeEnum.PACKAGE);
    }

    switch (packageType) {
      case PackageTypeEnum.PALLET:
        element.get('length').disable({emitEvent: false});
        element.get('length').reset(120, );
        element.get('width').disable({emitEvent: false});
        element.get('width').reset(80, );
        break;

      case PackageTypeEnum.HALF_PALLET:
        element.get('length').disable({emitEvent: false});
        element.get('length').reset(60, );
        element.get('width').disable({emitEvent: false});
        element.get('width').reset(80, );
        break;

      default:
        if (!isAfterInit) {
          element.get('length').enable({emitEvent: false});
          element.get('length').reset('', {emitEvent: false});
          element.get('width').enable({emitEvent: false});
          element.get('width').reset('', {emitEvent: false});
        }
        break;
    }
  }

  /**
   * Package Templates
   */
  makePackageTemplateForm() {
    this.packageTemplateForm = new FormGroup({
      uuid: new FormControl(null),
      packageTemplate: new FormControl(''),
      name: new FormControl('')
    });
  }

  resetPackageTemplateForm() {
    this.packageTemplateForm.get('uuid').reset(null);
    this.packageTemplateForm.get('packageTemplate').reset('');
    this.packageTemplateForm.get('name').reset('');
  }

  togglePackageTemplateForm() {
    this.showPackageTemplateForm = !this.showPackageTemplateForm;
  }

  handlePackageTemplateChange({value}) {
    const template = this.packageTemplates.find(item => item.uuid === value);

    if (!template) {
      this.packageTemplateForm.get('uuid').setValue(null);
      this.packageTemplateForm.get('name').setValue('');
      this.showDeletePackageTemplateButton = false;
      return;
    }

    const packagesArray = this.parent.get('packages') as FormArray;
    this.packageTemplateForm.get('uuid').setValue(template.uuid);
    this.packageTemplateForm.get('name').setValue(template.name);
    // console.log(this.parent);
    // console.log(this.parent.get('packages'));

    for (let i = 0; i < packagesArray.length; i++) {
      const packageDetails = packagesArray.at(i);
      packageDetails.get('type').setValue(template.package_type_id === null ? '' : template.package_type_id.toString());
      packageDetails.get('height').setValue(template.height);
      packageDetails.get('weight').setValue(template.weight);
      packageDetails.get('quantity').setValue(template.quantity);
      packageDetails.get('stackable').setValue(template.stackable);

      switch (template.package_type_id) {
        case PackageTypeEnum.PALLET:
          packageDetails.get('length').disable({emitEvent: false});
          packageDetails.get('length').reset(120);
          packageDetails.get('width').disable({emitEvent: false});
          packageDetails.get('width').reset(80);
          break;

        case PackageTypeEnum.HALF_PALLET:
          packageDetails.get('length').disable({emitEvent: false});
          packageDetails.get('length').reset(60);
          packageDetails.get('width').disable({emitEvent: false});
          packageDetails.get('width').reset(80);
          break;

        default:
          packageDetails.get('length').enable({emitEvent: false});
          packageDetails.get('length').reset('', {emitEvent: false});
          packageDetails.get('width').enable({emitEvent: false});
          packageDetails.get('width').reset('', {emitEvent: false});
          packageDetails.get('length').setValue(template.length);
          packageDetails.get('width').setValue(template.width);
          break;
      }
    }

    this.showDeletePackageTemplateButton = true;
  }

  savePackageTemplate() {
    const template = this.makeTemplate();

    if (!template.uuid) {
      // Store template
      this.storePackageTemplate(template);
    } else {
      // Update template
      this.updatePackageTemplate(template);
    }
  }

  makeTemplate() {
    return {
      'uuid': this.packageTemplateForm.get('uuid').value,
      'name': this.packageTemplateForm.get('name').value,
      'package_type_id': !this.packageDetails[0].get('type').value ? null : parseInt(this.packageDetails[0].get('type').value, 10),
      'length': !this.packageDetails[0].get('length').value ? null : parseInt(this.packageDetails[0].get('length').value, 10),
      'width': !this.packageDetails[0].get('width').value ? null : parseInt(this.packageDetails[0].get('width').value, 10),
      'height': !this.packageDetails[0].get('height').value ? null : parseInt(this.packageDetails[0].get('height').value, 10),
      'weight': !this.packageDetails[0].get('weight').value ? null : parseInt(this.packageDetails[0].get('weight').value, 10),
      'quantity': !this.packageDetails[0].get('quantity').value ? null : parseInt(this.packageDetails[0].get('quantity').value, 10),
      'stackable': this.packageDetails[0].get('stackable').value
    };
  }

  getAllPackageTemplates() {
    this.packageTemplates = [];
    this.mappedPackageTemplatesArray = [];
    this.packageTemplatesService.index().subscribe(templates => {
      if (templates.length > 0) {
        this.packageTemplates = templates;
        this.mappedPackageTemplatesArray = templates.map(template => {
          return {
            label: template.name,
            value: template.uuid
          };
        });
        this.mappedPackageTemplatesArray.unshift({
          label: this.translate.instant('account.section.createConsignment.packageTemplate.createNew'),
          value: null
        });
        this.showPackageTemplateForm = true;
      }
    });
  }

  storePackageTemplate(template) {
    this.packageTemplatesService.store(template).subscribe(response => {
      if (response) {
        this.packageTemplates.push(response);
        this.mappedPackageTemplatesArray.push({
          label: response.name,
          value: response.uuid
        });
        if (this.mappedPackageTemplatesArray.length === 1) {
          this.mappedPackageTemplatesArray.unshift({
            label: this.translate.instant('account.section.createConsignment.packageTemplate.createNew'),
            value: null
          });
        }
        this.showPackageTemplateForm = true;
        this.showDeletePackageTemplateButton = true;
        this.packageTemplateForm.get('uuid').setValue(response.uuid);
        this.packageTemplateForm.get('packageTemplate').setValue(response.uuid);
      }
    });
  }

  updatePackageTemplate(template) {
    this.packageTemplatesService.update(template.uuid, template).subscribe(response => {
      this.getAllPackageTemplates();
    });
  }

  destroyPackageTemplate() {
    this.packageTemplatesService.destroy(this.packageTemplateForm.get('uuid').value).subscribe(response => {
      this.showDeletePackageTemplateButton = false;
      this.resetPackageTemplateForm();
      this.getAllPackageTemplates();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/search/components/package-details-form/package-details-form.component.ts